import boddVariables from '../../../variables.module.scss';

interface WellnessReferenceItem {
    name: string,
    unit?: string,
    valuePostfix?: string,
    description?: string,
    hidden?: boolean,
    decimalPlaces?: number,
    min?: number,
    max?: number,
}

export interface WellnessItem {
    name: string,
    value: number,
    formattedValue: string,
    formattedWithPostfix: string,
    unitLabel?: string,
    min: number | undefined,
    max: number | undefined,
    dialGroup: DialGroup | undefined,
}

export const clamp = (value: number, max: number) => {
    if (value > max) return max;
    return value;
};

const wellnessReference: { [key: string]: WellnessReferenceItem } = {
    gender: { name: 'Gender', hidden: true },
    weight: { name: 'Weight', unit: 'kg', valuePostfix: ' kg' },
    height: { name: 'Height', unit: 'cm', valuePostfix: ' cm' },
    heartRate: { name: 'Heart Rate', unit: 'beats/min', valuePostfix: ' beats/min', min: 0, max: 140 },
    breathing: { name: 'Breathing', unit: 'breaths/min', valuePostfix: ' breaths/min', min: 0, max: 35 },
    systolicBP: { name: 'Systolic BP', valuePostfix: ' mmHg', decimalPlaces: 0 },
    diastolicBP: { name: 'Diastolic BP', valuePostfix: ' mmHg', decimalPlaces: 0 },
    heartRateVariability: { name: 'Heart Rate Variability', unit: 'ms', valuePostfix: ' ms', min: 1, max: 80 },
    irregularHeartBeats: { name: 'Irregular heartbeat Count', valuePostfix: '', decimalPlaces: 0 },
    cardiacWorkload: { name: 'Cardiac Workload', unit: 'dB', valuePostfix: ' dB', min: 3.71, max: 4.28 },
    vascularCapacity: { name: 'Vascular Capacity', unit: 'sec', valuePostfix: ' sec', min: 0.3, max: 3 },
    bmi: { name: 'Body Mass Index', unit: 'kg/m²', valuePostfix: ' kg/m²', min: 10, max: 60 },
    waistToHeight: { name: 'Waist-to-Height Ratio', unit: '%', valuePostfix: '%', min: 25, max: 70 },
    bodyShape: { name: 'Body Shape Index', valuePostfix: '/8.83', decimalPlaces: 2, min: 6.19, max: 8.83 },
    facialAge: { name: 'Facial Skin Age', valuePostfix: ' years', decimalPlaces: 0 },
    cardioDiseaseRisk: { name: 'Cardiac Disease Risk', valuePostfix: '%' },
    heartAttackRisk: { name: 'Heart Attack Risk', valuePostfix: '%' },
    strokeRisk: { name: 'Stroke Risk', valuePostfix: '%' },
    hyperTensionRisk: { name: 'Hypertension Risk', valuePostfix: '%' },
    diabetesRisk: { name: 'Type 2 Diabetes Risk', valuePostfix: '%' },
    hypercholesterolemiaRisk: { name: 'Hypercholesterolemia Risk', valuePostfix: '%' },
    hypertriglyceridemiaRisk: { name: 'Hypertriglyceridemia Risk', valuePostfix: '%' },
    hemoglobinA1cRisk: { name: 'Hemoglobin A1c Risk', valuePostfix: '%' },
    fastingBCRisk: { name: 'Fasting Blood Glucose Risk', valuePostfix: '%' },
    vitalScore: { name: 'Vitals', valuePostfix: '/5.0', decimalPlaces: 1 },
    physicalScore: { name: 'Physical', valuePostfix: '/5.0', decimalPlaces: 1 },
    physiologicalScore: { name: 'Physiological', valuePostfix: '/5.0', decimalPlaces: 1 },
    mentalStressIndex: { name: 'Mental Stress Index', valuePostfix: '/5.9', decimalPlaces: 1 },
    mentalScore: { name: 'Mental', valuePostfix: '/5.0', decimalPlaces: 1 },
    risksScore: { name: 'Risks', valuePostfix: '/5.0', decimalPlaces: 1 },
    wellnessScore: { name: 'General Wellness', valuePostfix: '/100', decimalPlaces: 0 },
    snr: { name: 'SNR', valuePostfix: ' dB' },
};

export enum DialGroup {
    NeedsAttention,
    Poor,
    Average,
    Good,
    Excellent,
}

const getColourForDialGroup = (dialGroup: DialGroup) => {
    switch (dialGroup) {
        case DialGroup.NeedsAttention: return boddVariables.needsAttention;
        case DialGroup.Poor: return boddVariables.poor;
        case DialGroup.Average: return boddVariables.average;
        case DialGroup.Good: return boddVariables.good;
        case DialGroup.Excellent: return boddVariables.excellent;
        default: return boddVariables.white;
    }
};

export const getLabelForDialGroup = (dialGroup: DialGroup) => {
    switch (dialGroup) {
        case DialGroup.NeedsAttention: return 'Needs Attention';
        case DialGroup.Poor: return 'Poor';
        case DialGroup.Average: return 'Average';
        case DialGroup.Good: return 'Good';
        case DialGroup.Excellent: return 'Excellent';
        default: return '';
    }
};

export const getLabelForBloodPressure = (systolic: number, diastolic: number) => {
    if (systolic < 70 || diastolic < 40) return 'Low';
    if (systolic < 90 && diastolic < 60) return 'Low';
    if (systolic >= 90 && systolic < 120 && diastolic >= 60 && diastolic < 80) return 'Good';
    return 'High';
};

export const getColourForBloodPressureLabel = (label: string) => {
    switch (label) {
        case 'Low': return boddVariables.average;
        case 'Good': return boddVariables.excellent;
        case 'High': return boddVariables.needsAttention;
        default: return boddVariables.white;
    }
};

export const getDialGroup = (name: string, value: number, sex?: string) => {
    switch (name) {
        case 'heartRate':
            if (value >= 60 && value < 100) return DialGroup.Excellent;
            return DialGroup.Average;
        case 'breathing':
            if (value >= 12 && value < 25) return DialGroup.Excellent;
            return DialGroup.Average;
        case 'heartRateVariability':
            if (value < 10.8) return DialGroup.NeedsAttention;
            if (value < 16.4) return DialGroup.Poor;
            if (value < 35.5) return DialGroup.Average;
            if (value < 51.5) return DialGroup.Good;
            return DialGroup.Excellent;
        case 'cardiacWorkload':
            if (value < 3.8) return DialGroup.Excellent;
            if (value < 3.9) return DialGroup.Good;
            if (value < 4.08) return DialGroup.Average;
            if (value < 4.18) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        case 'vascularCapacity':
            if (value < 0.79) return DialGroup.NeedsAttention;
            if (value < 1.12) return DialGroup.Poor;
            if (value < 1.78) return DialGroup.Average;
            if (value < 2.11) return DialGroup.Good;
            return DialGroup.Excellent;
        case 'bmi':
            if (value < 18.5) return DialGroup.Average;
            if (value < 25.0) return DialGroup.Excellent;
            if (value < 30.0) return DialGroup.Average;
            if (value < 35.0) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        case 'waistToHeight':
            if (sex === 'Female') {
                if (value < 42) return DialGroup.Average;
                if (value < 49) return DialGroup.Excellent;
                if (value < 54) return DialGroup.Average;
                if (value < 58) return DialGroup.Poor;
                return DialGroup.NeedsAttention;
            }
            if (value < 43) return DialGroup.Average;
            if (value < 53) return DialGroup.Excellent;
            if (value < 58) return DialGroup.Average;
            if (value < 63) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        case 'bodyShape':
            if (sex === 'Male') {
                if (value < 7.10) return DialGroup.Excellent;
                if (value < 7.60) return DialGroup.Good;
                if (value < 8.60) return DialGroup.Average;
                if (value < 9.10) return DialGroup.Poor;
                return DialGroup.NeedsAttention;
            }
            if (value < 6.80) return DialGroup.Excellent;
            if (value < 7.40) return DialGroup.Good;
            if (value < 8.60) return DialGroup.Average;
            if (value < 9.20) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        case 'wellnessScore':
            if (value < 20) return DialGroup.NeedsAttention;
            if (value < 40) return DialGroup.Poor;
            if (value < 60) return DialGroup.Average;
            if (value < 80) return DialGroup.Good;
            return DialGroup.Excellent;
        case 'mentalStressIndex':
            if (value < 2) return DialGroup.Excellent;
            if (value < 3) return DialGroup.Good;
            if (value < 4) return DialGroup.Average;
            if (value < 5) return DialGroup.Poor;
            return DialGroup.NeedsAttention;
        default:
            return undefined;
    }
};

export const formatWellnessData = (rawData: any) => {
    if (rawData) {
        return Object.keys(rawData).reduce((a, c) => {
            if (rawData[c] && !(wellnessReference[c]?.hidden)) {
                const isNumber = typeof rawData[c] === 'number';
                // default to 1 decimal place if not specified
                const decimalPlaces = wellnessReference[c].decimalPlaces ?? 1;
                const val = isNumber ? rawData[c].toFixed(decimalPlaces) : rawData[c];
                a[c] = {
                    name: wellnessReference[c]?.name ?? c,
                    value: rawData[c],
                    formattedValue: val,
                    formattedWithPostfix: `${val}${wellnessReference[c]?.valuePostfix ?? ''}`,
                    dialGroup: getDialGroup(c, rawData[c], rawData.gender),
                    unitLabel: wellnessReference[c].unit,
                    min: wellnessReference[c].min,
                    max: wellnessReference[c].max,
                };
            }
            return a;
        }, {} as { [key: string]: WellnessItem });
    }
    return {};
};

export default getColourForDialGroup;
